import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import {
  RoadStretchList,
  RoadStretchRows,
  RoadStretchLabel,
  RoadStretchRow,
  RoadStretchName,
  RoadStretchRange,
} from '../MainStyledComponents';
import {
  useRoadNamesById,
  useRoadRangesById,
} from '../../../../hooks/useRoadDirectionLabels';
import {
  formatPK,
  resolveRouteRoadRanges,
} from '../../../../utils/surveillanceRoute';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

// Sentry
import * as Sentry from '@sentry/react';

// Types
import { OTObject, ShowOTProps, GroupTypes } from './types';

// Styles Components
import {
  ContainerContent,
  ContainerData,
  ContainerDescription,
  RouteNameRow,
  ContainerOT,
  DateInput,
  InputDescription,
  RefRoid,
  RightArrow,
  ButtonSave,
  ButtonEdit,
  SpecialTagStyled,
  LocationGroup,
  RoadGroup,
  PkGroup,
  ClassificationTypeGroup,
  ConcessionWrapper,
  RouteLinksRow,
} from './styles';
// P15 rework bidireccional (2026-07-09) — pill de vínculo = SpecialTag de
// color. Opción C (2026-07-10): las acciones ver/editar viven en un menú
// anclado con filas táctiles (TagActionsMenu), no en mini-iconos de 16px.
import { SpecialTag } from '../../SpecialTag';
import { TagActionsMenu } from '../../SpecialTag/TagActionsMenu';

// Icons
import ArrowRight from '../../../../assets/icons/triangle-right.svg';

// Media
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import {
  Controller,
  ControllerProps,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import {
  useLocation,
  useNavigate,
  useParams,
  useSubmit,
} from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

// GQL
import { client } from '../../../../gql/client';
import {
  GET_SUB_CAPITOLS_BY_IDS,
  GET_CAPITOL_BY_ID,
  GET_SUB_CAPITOL_BY_ID,
} from '../../../../gql/queries/orders/predefinedData';

// Components
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import SearchableDropdown from '../../../inputs/SearchableDropdown';

// Types
import { OptionSelected } from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';

// Forms
import { schemaOT } from './schema';
import { createFormDataFromValues } from '../../../../utils/forms';
import {
  GET_CONCESSION_BY_ID,
  GET_ROADS_BY_IDS,
  GET_ROAD_BY_ID,
} from '../../../../gql/queries/geoPositions/roads';
import { difference } from '../../../../utils/difference';
import { Typography } from '../../Typography';
import InputText from '../../../inputs/InputText';
import { pkFormatter } from '../../../../utils/transform';
import { variant } from '../../SpecialTag/types';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../modals/SuperModal/types';
import {
  ContainerHeader,
  ContainerRefIcon,
  ContainerTitleArrow,
  TagsContainer,
  TitleDate,
  TitleOrder,
} from '../MainStyledComponents';
import { ArrowRadioGroup } from '../../../../pages/main/comunications/NewComunication/styles';
import { PartialDirection } from '../../../inputs/buttons/ArrowDirectionButton/types';
import { ActionDirection } from '../../../forms/ActionForms/LocationForm/types';
import { UNSPECIFIC } from '../../../../types/utils';
import { Margin } from '../../../../types/workOrder';
import { useAuth } from '../../../../hooks/useAuth';
import { useSurveillanceRouteOwnership } from '../../../../hooks/useSurveillanceRouteOwnership';
import { Role } from '../../../../types/auth';
import {
  canEditOrderRow,
  canRoleChangeStateOf,
} from '../../../../utils/rolePermissions';
import { OrderState, OrderType } from '../../../../types/orders';
import { maxAvisoEndDateFor } from '../../../../utils/avisoCloseWindow';
import { useSurveillanceRoutes } from '../../../../hooks/useSurveillanceRoutes';
import {
  canEditSurveillanceRoute,
  canLinkExternalOrder,
  canOperateTimer,
} from '../../../../utils/surveillanceRoute';
import { registerPk } from '../../../../utils/pkField';
import {
  translateLaneOptions,
  translateTrackOptions,
  type CatalogEntry,
} from '../../../../utils/catalogLabels';

// `PRIVILEGED_EDIT_ROLES` y la regla rol×estado del lápiz vivían aquí escritas
// a mano. Desde 2026-08-04 son `canEditOrderRow` en `utils/rolePermissions.ts`,
// porque el botón "Editar comunicación" de las tablas de OT aplica exactamente
// la misma regla y dos copias divergirían.

export const WorkOrder = ({
  id,
  name,
  roid,
  type,
  tag,
  date,
  derivedEndDate,
  capitol,
  subCapitol,
  concession,
  concessions,
  road,
  routeRoadIDs,
  routeRoadRanges,
  roadsValues,
  capitols,
  pkInitRoad,
  pkEndRoad,
  pkInit,
  pkEnd,
  description,
  notes,
  subCapitolsValues,
  modelTypeId,
  revalidatorFunction,
  indexExpedition,
  state,
  closedAt,
  classification,
  classifications,
  disabled,
  groupTracks,
  track,
  lane,
  groupLanes,
  margin,
  direction,
  userName,
  predefine,
  predefinedOrders,
  teams,
  teamRelationships,
  isSurveillanceRouteOwnOrder,
  routeName,
  linkedRvRoid,
  linkedRvRouteId,
  linkedSegmentEntityId,
  linkedRoutes = [],
  linkedExternalOrders = [],
  canLinkOrders = false,
  onLinkOrder,
  onUnlinkOrder,
  onViewLinkedOrder,
}: ShowOTProps): JSX.Element => {
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const dispatch = useDispatch();
  const { currentRole, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // D3 (2026-07-20): id del RV de la URL actual (contexto de ruta
  // `/surveillance-route/:routeId/child-orders`) — target de navegación del
  // lápiz en modo RV. Trampa evitada: `modelTypeId` TAMBIÉN vale `srData.id`
  // en este branch del selector, pero es un campo overloaded (genéricas
  // publican `genericOrdersIDs[0]`, campañas `campaignIDs[0]`) — reusar el id
  // que ya vive en la URL es la fuente correcta y desacoplada del selector.
  const { routeId: routeIdFromUrl } = useParams<{ routeId?: string }>();
  const {
    linkExternalOrderToSegment,
    unlinkOrderFromSegment,
    unlinkOrderFromSurveillanceRoute,
  } = useSurveillanceRoutes();
  const [showInfo, setShowInfo] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const toggleInfo = useCallback(() => setShowInfo(!showInfo), [showInfo]);
  const toggleEdit = useCallback(
    () => setIsDisabled(!isDisabled),
    [isDisabled]
  );
  const submit = useSubmit();
  const subCapitolDropDownRef = useRef<ImperativeRefInput>(null);
  const capitolDropDownRef = useRef<ImperativeRefInput>(null);

  const isLandscapeTablet = useIsLandscapeTablet();
  // Feature flag para mostrar/ocultar campo margen
  const showMargin = useFeatureFlag('showMargin', true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialData: OTObject = {
    capitols,
    concessions,
    date,
    pkInitRoad,
    pkEndRoad,
    pkInit,
    pkEnd,
    description,
    notes,
    capitol,
    concession,
    road,
    roads: roadsValues,
    subCapitol,
    subCapitols: subCapitolsValues,
    classification,
    classifications,
    groupTracks,
    track,
    lane,
    groupLanes,
    margin,
    direction,
    predefine,
    predefinedOrders,
    teams,
    teamRelationships,
  };

  const {
    handleSubmit,
    control,
    setValue,
    register,
    formState: { errors },
  } = useForm<OTObject>({
    defaultValues: initialData,
    resolver: yupResolver(schemaOT) as any,
    // El detalle de genérica/campaña requiere equipo cuando hay equipos
    // disponibles (campo visible). Activa la regla yup `teamRelationships` del
    // schemaOT compartido; SubWorkOrder pasa `requiresTeam: false`.
    // `isSurveillanceRoute` (2026-07-17): la gemela del RV no tiene capítulo/
    // subcapítulo (campos ocultos en esta card) — relaja sus required.
    context: {
      requiresTeam: Boolean(teams && teams.length > 0),
      isSurveillanceRoute: Boolean(isSurveillanceRouteOwnOrder),
    },
  });

  const roads = useWatch({
    control,
    name: 'roads',
  });

  const subCapitols = useWatch({
    control,
    name: 'subCapitols',
  });

  const watchedFields = useWatch({
    control,
    defaultValue: initialData,
  });

  const roadDirections = useWatch({
    control,
    name: 'roadDirections',
  });

  const onSelectCapitol = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      try {
        const { readSubCapitols } = client.readQuery({
          query: GET_SUB_CAPITOLS_BY_IDS,
          variables: {
            ids: option?.data ?? [],
          },
        });
        setValue('subCapitols', readSubCapitols);
        subCapitolDropDownRef.current?.clearSelected();
        setValue('subCapitol', '');
      } catch (err) {
        Sentry.captureException(err);
        console.log(err);
      }
      onChange(option?.value ?? '');
    },
    []
  );

  const onSelectPredefined = useCallback(
    (
      option: OptionSelected | undefined,
      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        onChange(option?.value ?? '');

        // Si no hay data del predefinido, no hacer nada más
        if (!option?.data?.capitolId) return;

        const [
          {
            data: { capitol },
          },
          {
            data: { subCapitol },
          },
        ] = await Promise.all([
          client.query({
            query: GET_CAPITOL_BY_ID,
            variables: { id: option?.data.capitolId },
          }),
          client.query({
            query: GET_SUB_CAPITOL_BY_ID,
            variables: { id: option?.data.subCapitolId },
          }),
        ]);

        // Actualizar lista de subCapitols basándose en el capitol
        const { readSubCapitols } = client.readQuery({
          query: GET_SUB_CAPITOLS_BY_IDS,
          variables: { ids: capitol?.subCapitolsIDs ?? [] },
        });
        setValue('subCapitols', readSubCapitols);

        // Auto-seleccionar Capitol
        capitolDropDownRef.current?.setSelectedOption({
          label: capitol.name,
          value: capitol.id,
          data: capitol.subCapitolsIDs,
        });

        // Auto-seleccionar SubCapitol
        subCapitolDropDownRef.current?.setSelectedOption({
          label: subCapitol.name,
          value: subCapitol.id,
          data: subCapitol.elementOrdersIds,
        });
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    [capitolDropDownRef, subCapitolDropDownRef]
  );

  const renderSubCapitols = useCallback<
    ControllerProps<OTObject, 'subCapitol'>['render']
  >(
    ({ field: { onChange }, formState: { defaultValues } }) => {
      const options =
        subCapitols?.map((subCapitol) => ({
          label: subCapitol?.name || '',
          value: subCapitol?.id || '',
          data: subCapitol?.elementOrdersIds || '',
        })) || [];
      return (
        <DropDownInput
          ref={subCapitolDropDownRef}
          inputSize="L"
          onChangeSelected={(option) => onChange(option?.value ?? '')}
          typeDropDown="Default"
          labelText={t('workOrder.subchapter')}
          placeholder={t('common:labels.select')}
          defaultValue={defaultValues?.subCapitol}
          options={options}
          isDisabled={isDisabled}
          borderError={!subCapitol && !!errors.subCapitol}
        />
      );
    },
    [subCapitols, isDisabled, showInfo]
  );

  const roadDropDownRef = useRef<ImperativeRefInput>(null);
  const onSelectConcession = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: option?.value },
        });

        const { readRoads } = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        });

        setValue('roads', readRoads);

        roadDropDownRef.current?.clearSelected();
        setValue('road', '');

        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const onSelectRoad = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { road },
        } = await client.query({
          query: GET_ROAD_BY_ID,
          variables: { id: option?.value },
        });

        setValue(
          'pkInitRoad',
          pkFormatter(road.pkInitKm, road.pkInitMeter, true)
        );
        setValue('pkEndRoad', pkFormatter(road.pkEndKm, road.pkEndMeter, true));
        setValue('roadDirections', road.directions);

        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  useEffect(() => {
    if (initialData?.road && roads) {
      const currentRoad =
        roads?.find((road) => road.name === initialData.road) ||
        roads?.find((road) => road.id === initialData.road);
      const tasks = async () => {
        // ✅ Agregar validación para evitar query con id undefined
        if (!currentRoad?.id) {
          console.warn(
            '🚨 currentRoad.id es undefined, saltando query GET_ROAD_BY_ID'
          );
          return;
        }

        const {
          data: { road },
        } = await client.query({
          query: GET_ROAD_BY_ID,
          variables: { id: currentRoad.id },
        });
        setValue(
          'pkInitRoad',
          pkFormatter(road.pkInitKm, road.pkInitMeter, true)
        );
        setValue('pkEndRoad', pkFormatter(road.pkEndKm, road.pkEndMeter, true));
        setValue('roadDirections', road.directions);
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    }
  }, []);

  // Acota el dropdown de carreteras a las de la concesión del order (UTE), igual que
  // `loadConcessionData` en NewWorkOrderForm/Description.tsx. El editor inline recibía
  // TODAS las carreteras (`roadsValues` = GET_ROADS) y no las filtraba por concesión,
  // dejando ver carreteras de otras concesiones. Solo se ejecuta una vez en el montaje.
  useEffect(() => {
    if (!concession) return;
    const filterRoadsByConcession = async () => {
      try {
        const {
          data: { concession: concessionData },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: concession },
        });

        const concessionRoadIds: string[] = concessionData?.roadIDs ?? [];
        if (concessionRoadIds.length === 0) return;

        const allowed = new Set(concessionRoadIds);
        const concessionRoads = (roadsValues ?? []).filter((road) =>
          allowed.has(road.id)
        );

        // Solo acotar si encontramos las carreteras de la concesión; si no, dejamos
        // la lista tal cual para no vaciar el dropdown ante datos inconsistentes.
        if (concessionRoads.length > 0) {
          setValue('roads', concessionRoads);
        }
      } catch (err) {
        Sentry.captureException(err);
        console.log('Error filtrando carreteras por concesión:', err);
      }
    };
    filterRoadsByConcession();
  }, []);

  const onFormSubmit: SubmitHandler<OTObject> = async () => {
    const dataDifference = difference(watchedFields, initialData) as Record<
      string,
      unknown
    >;
    // `difference()` diffea arrays por índice: no detecta fiablemente añadir
    // (devolvería solo el delta) ni quitar del final (no lo ve). Como el backend
    // reemplaza los equipos (set:[] + connect), comparamos teamRelationships
    // aparte y, si cambió, enviamos el array COMPLETO; si no, lo excluimos.
    const currentTeams = ((watchedFields as { teamRelationships?: string[] })
      .teamRelationships ?? []) as string[];
    const initialTeams = ((initialData as { teamRelationships?: string[] })
      .teamRelationships ?? []) as string[];
    // El "equipo obligatorio (mín. 1)" lo valida yup vía schemaOT (`$requiresTeam`),
    // que bloquea `handleSubmit` antes de llegar aquí — igual que el resto de campos.
    const teamsChanged =
      [...currentTeams].sort().join(',') !== [...initialTeams].sort().join(',');
    if (teamsChanged) {
      dataDifference.teamRelationships = currentTeams;
    } else {
      delete dataDifference.teamRelationships;
    }
    if (Object.keys(dataDifference).length > 0) {
      setIsDisabled(true);
      const formData = createFormDataFromValues({
        ...dataDifference,
        id,
        roid,
        modelTypeId,
        type,
      });
      submit(formData, {
        method: 'POST',
        action: '.',
      });
    } else {
      setErrorMessage(
        'You must make at least one change to the form before proceeding'
      );
    }
  };

  // RV (2026-07-20): la card de la propia gemela del RV viaja con
  // `type: Generic` forzado (reuso de render) — SIN este branch la pill abría
  // el modal con la máquina GENÉRICA (Gestionada/Pendiente de trabajo…) y
  // submit UPDATE_ORDER, saltándose la matriz D5 y las cascadas a tramos.
  // Con él: orderType SurveillanceRoute + routeId (modelTypeId = id de la
  // entity SR) → el modal enruta a las mutations SR. Gate por matriz D5.
  //
  // Gate unificado (2026-08-20). Esta tarjeta ya preguntaba «¿hay destinos?»
  // para el RV — el patrón correcto — pero para el resto se conformaba con
  // «¿el rol toca este TIPO?», que es lo que dejaba al Tablet abrir el modal
  // de una actuación y encontrárselo vacío. `canRoleChangeStateOf()` hace las
  // dos cosas: lleva dentro la matriz D5 del RV (incluido el recorte de las
  // transiciones que solo puede hacer el cronómetro) y para lo demás mira los
  // destinos reales en vez del tipo.
  //
  // Quién y de quién (2026-09-23): la card gemela no trae equipo ni dueño del
  // recorrido; se leen de la caché, donde el loader del detalle lo acaba de
  // dejar.
  const routeOwnership = useSurveillanceRouteOwnership(
    isSurveillanceRouteOwnOrder ? routeIdFromUrl : null
  );
  const modalOrderType = isSurveillanceRouteOwnOrder
    ? OrderType.SurveillanceRoute
    : (type ?? '');
  // 2026-10-22: en la tarjeta del RECORRIDO, una tablet solo cambia el estado
  // de un recorrido que OPERA — la misma regla que la pastilla de la cabecera
  // (`RouteHeaderCard.canChangeSrState`) y que el lápiz de esta tarjeta. Era
  // el único sitio desde el que una tablet entregaba el recorrido vivo de la
  // OTRA patrulla. Sin el dato de propiedad en caché, la tablet no puede
  // (lado seguro): la dueña conserva «Finalizar» en la cabecera.
  const canChangeState =
    canRoleChangeStateOf(
      currentRole as Role,
      modalOrderType,
      state as unknown as OrderState
    ) &&
    (!isSurveillanceRouteOwnOrder ||
      currentRole !== Role.Tablet ||
      canOperateTimer(user?.user, routeOwnership));

  const openModalChangeState: React.MouseEventHandler<HTMLDivElement> = (
    event
  ) => {
    if (!canChangeState) return;
    dispatch(
      openModal(modalIds.CHANGE_STATE_MODAL, {
        indexExpedition,
        revalidatorFunction,
        currentState: state,
        orderType: isSurveillanceRouteOwnOrder
          ? OrderType.SurveillanceRoute
          : (type ?? undefined),
        orderId: id,
        ...(isSurveillanceRouteOwnOrder && {
          routeId: modelTypeId,
          // Para el modal de cierre de tramo si la entrega lo requiere.
          roadId: road,
        }),
      })
    );

    event.stopPropagation();
  };

  // D3 (2026-07-20, Addendum "Card gemela → flujo de editar"): la card
  // gemela deja de editar inline en modo RV — el lápiz navega al wizard
  // EditRoute. Matriz ÚNICA de gating: `canEditSurveillanceRoute` (la misma
  // que usa `RouteHeaderCard`), NO el gating canónico de genéricas de más
  // abajo (`ButtonEdit disabled`). Causa raíz del bug que esto arregla: el
  // gating de genéricas solo reconoce la excepción Tablet vía
  // `tag === variant.WorkInProgress`, pero los estados propios del RV
  // (`in-progress`/`paused`, variantes dedicadas desde 2026-07-20) nunca
  // producen ese tag → Tablet quedaba bloqueado en un RV `in-progress` que
  // el wizard SÍ le permite editar.
  const canEditRoute = canEditSurveillanceRoute(
    currentRole,
    state,
    user?.user,
    routeOwnership
  );
  const handleEditRoute = useCallback(() => {
    if (!routeIdFromUrl) return;
    navigate(
      `/surveillance-route/${routeIdFromUrl}/edit?returnTo=${encodeURIComponent(
        location.pathname
      )}`
    );
  }, [navigate, routeIdFromUrl, location.pathname]);

  // ─── P15 Fase 2 (2026-07-09) — pill "Vincular a recorregut" / "Vinculat
  // amb Su.NN" (patrón Transferir, opción A). Ver
  // docs/superpowers/specs/2026-07-09-p15-fase2-external-link-design.md. ───

  // Desde 2026-07-21 la card del propio RV llega con su type REAL
  // (`surveillanceRoute` — el selector ya no lo reescribe a `generic`), así
  // que `isLinkableOrderType` la excluye por el propio type y nunca muestra
  // la CTA "Vincular a recorregut" contra sí misma. El guard de contexto por
  // prefijo de ruta se conserva como defensa redundante — mismo criterio que
  // usa `ShowOT/index.tsx` para `isSurveillanceRouteContext`.
  /** «82 + 800» o «82+800» → sus partes. `undefined` si no es un PK. */
  const parsePkToParts = (
    pk?: string
  ): { km: number; meter: number } | undefined => {
    const m = (pk ?? '').replace(/\s+/g, '').match(/^(\d+)\+(\d{1,4})$/);
    return m ? { km: Number(m[1]), meter: Number(m[2]) } : undefined;
  };

  // Multi-carretera: una fila por vía del recorrido, con su rango. Mismo dato y
  // mismo criterio que la vista previa (`resolveRouteRoadRanges`), que es donde
  // se decidió que el rango va SIEMPRE pegado a su carretera.
  const routeRoadIdsOfOrder = isSurveillanceRouteOwnOrder
    ? (routeRoadIDs ?? [])
    : [];
  const orderRoadNames = useRoadNamesById(routeRoadIdsOfOrder);
  const orderRoadRanges = useRoadRangesById(routeRoadIdsOfOrder);
  const routeRoadRangeRows = useMemo(
    () =>
      resolveRouteRoadRanges(
        {
          roadIDs: routeRoadIdsOfOrder,
          roadRanges: routeRoadRanges,
          // Los PK escalares del recorrido, que son los de su PRIMERA vía y es
          // justo lo que `resolveRouteRoadRanges` usa para ella.
          pkInitKm: parsePkToParts(pkInit)?.km,
          pkInitMeter: parsePkToParts(pkInit)?.meter,
          pkEndKm: parsePkToParts(pkEnd)?.km,
          pkEndMeter: parsePkToParts(pkEnd)?.meter,
        },
        orderRoadRanges
      ).map((r) => ({
        roadId: r.roadId,
        name: orderRoadNames[r.roadId] || r.roadId,
        // Entre paréntesis, igual que en la vista previa del recorrido: es el
        // mismo dato y no debería leerse de dos formas distintas.
        range: `(${formatPK(r.pkInitKm, r.pkInitMeter)} → ${formatPK(
          r.pkEndKm,
          r.pkEndMeter
        )})`,
      })),
    [routeRoadIdsOfOrder, orderRoadNames, orderRoadRanges, routeRoadRanges, pkInit, pkEnd]
  );

  /**
   * Cuándo se enseña la lista de vías en vez del desplegable + las cajas de PK.
   *
   * 🔴 Solo en modo RV: en genéricas y campañas ese desplegable SÍ edita, y ahí
   * no se toca nada. Con una sola vía tampoco — sus cajas de siempre ya la
   * cuentan bien.
   */
  const showRoadStretches =
    isSurveillanceRouteOwnOrder && routeRoadRangeRows.length > 0;

  /**
   * El selector de Equipo, extraído para poder colocarlo en un sitio distinto
   * según la tarjeta.
   *
   * En el RV baja a la fila del Nombre del recorrido: arriba, entre campos de
   * una línea, era lo que abría el hueco vertical que dejaba la fila coja. En
   * genéricas y campañas se queda donde estaba.
   */
  const teamField =
    teams && teams.length > 0 ? (

              <Controller
                control={control}
                name="teamRelationships"
                render={({ field: { onChange, value } }) => {
                  const options = teams.map((team) => ({
                    label: team?.name || '',
                    value: team?.id || '',
                  }));
                  // El valor llega como array de teamIDs (selectors/orders.ts).
                  // Tolera el legacy single (string) por si acaso.
                  const selectedValues = Array.isArray(value)
                    ? value
                    : value
                      ? [value as string]
                      : [];
                  return (
                    <MultiSelectDropdown
                      inputSize="L"
                      onChangeSelected={(ids) => onChange(ids)}
                      typeDropDown="Default"
                      labelText={t('workOrder.team')}
                      placeholder={t('common:select')}
                      selectedValues={selectedValues}
                      options={options}
                      // Mismo patrón que los demás campos del detalle (p.ej.
                      // `!pkInit && !!errors.pkInit`): rojo SOLO si la ficha cargó
                      // SIN equipo. Si ya tenía equipo y lo borras, no se pinta
                      // rojo — yup bloquea el guardado y RHF enfoca igual que el
                      // resto. En crear, el form arranca vacío → sí se pinta rojo.
                      borderError={
                        !teamRelationships?.length && !!errors.teamRelationships
                      }
                      isDisabled={isDisabled}
                    />
                  );
                }}
              />
    ) : null;


  const isSurveillanceRouteContext = location.pathname.startsWith(
    '/surveillance-route/'
  );
  const isLinkableOrderType =
    (type === OrderType.Generic || type === OrderType.Campaign) &&
    !isSurveillanceRouteContext;
  const canLinkExternal = canLinkExternalOrder(currentRole);
  // Una anulada nunca se liga a un aviso; una cerrada solo si sabemos cuándo
  // (las cerradas antes del historial no tienen `closedAt` y no se ofrecen).
  // Tope de «Fecha final» de los avisos; `null` si no hay `closedAt` válido.
  const closedAvisoCap = closedAt ? maxAvisoEndDateFor(closedAt) : null;
  const isLinkableByState =
    state !== OrderState.Annulled &&
    (state !== OrderState.End || closedAvisoCap !== null);

  // `event` opcional: la CTA (SpecialTag clicable) sí lo pasa para cortar el
  // toggle de la cabecera; las filas del TagActionsMenu ya hacen su propio
  // stopPropagation y llaman sin argumentos.
  const handleOpenRelateExternalModal = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      dispatch(
        openModal(modalIds.RELATE_SEGMENT, {
          linkKind: 'external',
          defaultPauseCauses: ['external-call'],
          currentContextOrderId: id,
          // OT cerrada: solo se ofrecen los avisos de antes del cierre.
          maxAvisoEndDate:
            state === OrderState.End ? closedAvisoCap : undefined,
          onConfirm: async (segmentId: string) => {
            await linkExternalOrderToSegment(segmentId, id);
            revalidatorFunction?.();
          },
        })
      );
    },
    [dispatch, id, state, closedAvisoCap, linkExternalOrderToSegment, revalidatorFunction]
  );

  // ── Cross-link 3 (§ 13.3, 2026-09-22): pills de los recorridos que
  //    trabajaron esta OT — desvincular (cascada a sus tramos) e ir al recorrido.
  const handleUnlinkRoute = useCallback(
    async (routeId: string) => {
      await unlinkOrderFromSurveillanceRoute(routeId, id);
      revalidatorFunction?.();
    },
    [id, unlinkOrderFromSurveillanceRoute, revalidatorFunction]
  );
  const handleViewRoute = useCallback(
    (routeId: string) => navigate(`/surveillance-route/${routeId}`),
    [navigate]
  );

  const handleNavigateToLinkedRoute = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      if (linkedRvRouteId) {
        navigate(`/surveillance-route/${linkedRvRouteId}`);
      }
    },
    [navigate, linkedRvRouteId]
  );

  // Opción A (2026-07-10): deshace el par — el tramo VUELVE a la cola de
  // pendientes (el backend restaura pendingExternalLink) y esta OT queda
  // sin vincular. El hook toastea éxito/error.
  const handleUnlinkFromSegment = useCallback(async () => {
    if (!linkedSegmentEntityId) return;
    await unlinkOrderFromSegment(linkedSegmentEntityId, id);
    revalidatorFunction?.();
  }, [linkedSegmentEntityId, id, unlinkOrderFromSegment, revalidatorFunction]);

  // Rango fecha-hora de la orden padre en la cabecera, igual que las filas hijas
  // (ActionOT): inicio = registerInitDate, fin = derivedEndDate (finalización
  // derivada de la última actuación). Sin derivedEndDate (orden sin actuaciones
  // aún) se muestra solo el inicio; sin fecha de inicio válida, no se muestra nada.
  const startDate = date ? new Date(date) : null;
  const isValidStart = !!startDate && !isNaN(startDate.getTime());
  const endDateValue = derivedEndDate ? new Date(derivedEndDate) : null;
  const isValidEnd = !!endDateValue && !isNaN(endDateValue.getTime());
  const sameDay =
    isValidStart &&
    isValidEnd &&
    (startDate as Date).toDateString() ===
      (endDateValue as Date).toDateString();
  const rangeLabel = isValidStart
    ? `${format(startDate as Date, 'dd/MM/yyyy - HH:mm')}${
        isValidEnd
          ? ` a ${
              sameDay
                ? format(endDateValue as Date, 'HH:mm')
                : format(endDateValue as Date, 'dd/MM/yyyy - HH:mm')
            }`
          : ''
      }`
    : '';

  // Etiqueta de estado. En casi todas las cards va la PRIMERA del grupo; en la
  // del recorrido va al final, junto a «Vincular OT» (ver abajo).
  const stateTag = (
    <SpecialTagStyled
      variant={tag}
      sizeTag={isLandscapeTablet ? 'small' : 'default'}
      onClick={canChangeState ? openModalChangeState : undefined}
      style={canChangeState ? undefined : { cursor: 'default', opacity: 0.7 }}
    />
  );

  return (
    <ContainerOT>
      <ContainerHeader onClick={toggleInfo}>
        <TitleOrder>
          {name}
          {rangeLabel && <TitleDate>{` ${rangeLabel}`}</TitleDate>}
        </TitleOrder>
        {/* Misma estructura que las cards hijas (ActionOT y compañía): el
            cluster ROID + chips es una columna alineada a la derecha en tablet
            vertical y la flecha queda fuera, centrada. Antes esta card metía
            todo en una sola fila y en vertical el título se comprimía a 4
            líneas con los chips desbordando. */}
        <ContainerTitleArrow>
          <ContainerRefIcon>
            <RefRoid size="S">{roid}</RefRoid>
            <TagsContainer>
              {/* ── Vínculos recorrido ↔ OT (§ 13.3, 2026-09-22) ──
                  Una pill verde POR RECORRIDO ligado, en orden cronológico
                  (`linkedAt`), con el desplegable táctil de siempre: Ver
                  recorrido y, para quien puede ligar, Desvincular (que en el
                  backend devuelve a «pendiente» los avisos de ese recorrido).
                  La CTA «Vincular a recorrido» se queda SIEMPRE visible para
                  quien puede ligar — antes desaparecía al ligar el primero y
                  «solo servía para una». */}
              {/* Feedback usuario 2026-09-24: mismo patrón que la card del
                  recorrido — los recorridos vinculados en su propia fila y el
                  estado abajo, JUNTO a «Vincular a recorrido». Vale para OTs y
                  campañas (las dos usan esta card). */}
              {isLinkableOrderType &&
                (linkedRoutes.length > 0 || Boolean(linkedRvRoid)) && (
                  <RouteLinksRow>
                    {linkedRoutes.map((route) => (
                      <TagActionsMenu
                        key={route.id}
                        variant={variant.LinkedToSegment}
                        sizeTag={isLandscapeTablet ? 'small' : 'default'}
                        title={t('pages:surveillance.linkedWith', {
                          roid: route.roid || route.name || '',
                        })}
                        actions={[
                          {
                            icon: 'view',
                            label: t('pages:surveillance.seeRoute'),
                            onClick: () => handleViewRoute(route.id),
                          },
                          ...(canLinkExternal
                            ? [
                                {
                                  icon: 'unlink' as const,
                                  label: t('common:buttons.unlink'),
                                  onClick: () => handleUnlinkRoute(route.id),
                                },
                              ]
                            : []),
                        ]}
                      />
                    ))}
                    {/* Datos anteriores al cross-link 3: tramo ligado sin
                        recorrido ligado → la pill de siempre, con Ver y
                        Desvincular tramo. Sin «Editar» (§ 13.11): desde D7
                        vincular AÑADE un tramo en vez de cambiarlo, así que
                        «Editar» prometería algo que no hace; para cambiar de
                        tramo se desvincula y se vuelve a vincular con la CTA,
                        que está siempre visible. */}
                    {linkedRoutes.length === 0 && linkedRvRoid && (
                      <TagActionsMenu
                        variant={variant.LinkedToSegment}
                        sizeTag={isLandscapeTablet ? 'small' : 'default'}
                        title={t('pages:surveillance.linkedWith', {
                          roid: linkedRvRoid,
                        })}
                        actions={[
                          {
                            icon: 'view',
                            label: t('pages:surveillance.seeRoute'),
                            onClick: () => handleNavigateToLinkedRoute(),
                          },
                          ...(canLinkExternal && linkedSegmentEntityId
                            ? [
                                {
                                  icon: 'unlink' as const,
                                  label: t('common:buttons.unlink'),
                                  onClick: () => handleUnlinkFromSegment(),
                                },
                              ]
                            : []),
                        ]}
                      />
                    )}
                  </RouteLinksRow>
                )}
              {!isSurveillanceRouteOwnOrder && stateTag}
              {isLinkableOrderType && canLinkExternal && isLinkableByState && (
                // CTA garnet: la pill entera abre el modal de tramos con aviso
                // pendiente (un gesto liga tramo Y recorrido, § 13.4).
                <SpecialTag
                  variant={variant.LinkCta}
                  sizeTag={isLandscapeTablet ? 'small' : 'default'}
                  title={t('pages:surveillance.linkToRoute')}
                  onClick={handleOpenRelateExternalModal}
                  style={{ cursor: 'pointer' }}
                  data-testid="link-route-cta"
                />
              )}
              {/* ── Lado recorrido (§ 13.5): la card GEMELA del recorrido pinta
                  sus OTs con el mismo lenguaje que las cards de tramo. Los
                  handlers los pone la página (RouteChildOrders). */}
              {/* Feedback usuario 2026-09-24: las OTs vinculadas ocupan su propia
                  fila (a todo lo ancho del grupo) y el estado del recorrido
                  baja a la última, JUNTO a «Vincular OT»: estado + acción
                  siempre abajo a la derecha — donde cae el estado en las cards
                  de tramo — y la lista crece hacia arriba sin mover el botón. */}
              {isSurveillanceRouteOwnOrder && linkedExternalOrders.length > 0 && (
                <RouteLinksRow>
                  {linkedExternalOrders.map((link) => (
                    <TagActionsMenu
                      key={link.order.id}
                      variant={variant.LinkedFromSegment}
                      sizeTag={isLandscapeTablet ? 'small' : 'default'}
                      title={t('pages:surveillance.linkedWith', {
                        roid: link.order.roid || '',
                      })}
                      actions={[
                        {
                          icon: 'view',
                          label: t('common:buttons.view'),
                          onClick: () => onViewLinkedOrder?.(link),
                        },
                        ...(canLinkOrders && onUnlinkOrder
                          ? [
                              {
                                icon: 'unlink' as const,
                                label: t('common:buttons.unlink'),
                                onClick: () => onUnlinkOrder(link.order.id),
                              },
                            ]
                          : []),
                      ]}
                    />
                  ))}
                </RouteLinksRow>
              )}
              {isSurveillanceRouteOwnOrder && stateTag}
              {isSurveillanceRouteOwnOrder && canLinkOrders && onLinkOrder && (
                <SpecialTag
                  variant={variant.LinkCta}
                  sizeTag={isLandscapeTablet ? 'small' : 'default'}
                  title={t('pages:surveillance.linkOrder')}
                  onClick={(event) => {
                    event.stopPropagation();
                    onLinkOrder();
                  }}
                  style={{ cursor: 'pointer' }}
                  data-testid="link-order-cta"
                />
              )}
            </TagsContainer>
          </ContainerRefIcon>
          <RightArrow
            src={ArrowRight}
            alt={t('common:expandInfo')}
            showInfo={showInfo}
          />
        </ContainerTitleArrow>
      </ContainerHeader>
      {showInfo && (
        <ContainerContent onSubmit={handleSubmit(onFormSubmit)}>
          <ContainerData>
            <Controller
              control={control}
              name="date"
              render={({ field: { onChange } }) => {
                return (
                  <DateInput
                    label={t('workOrder.registerDate')}
                    labelHour={t('workOrder.hour')}
                    onChangeDate={(value) => onChange(value)}
                    initialDate={date}
                    borderError={!date && !!errors.date}
                    // RV: la fecha de registro la gobierna el timer del
                    // recorrido (start), no se edita desde la card.
                    isDisabled={
                      isDisabled || Boolean(isSurveillanceRouteOwnOrder)
                    }
                  />
                );
              }}
            />

            {userName && (
              <InputText
                label={t('forms:workOrder.createdBy')}
                typeInput="normal"
                disabled
                value={userName}
                inputSize={'L'}
              />
            )}

            {/* RV (2026-07-17): la gemela del RV no usa predefinidos ni
                capítulo/subcapítulo (el wizard no los recoge — FR-4.2 los
                omite en la práctica) — fuera de la card. */}
            {!isSurveillanceRouteOwnOrder && (
              <>
                <Controller
                  control={control}
                  name="predefine"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options =
                      defaultValues?.predefinedOrders?.map(
                        (predefinedOrder) => ({
                          label: predefinedOrder?.name || '',
                          value: predefinedOrder?.id || '',
                          data: {
                            capitolId: predefinedOrder?.capitolId || '',
                            subCapitolId: predefinedOrder?.subCapitolId || '',
                          },
                        })
                      ) || [];

                    // Si no hay predefinidos disponibles, no mostrar el campo
                    if (options.length === 0) {
                      return <></>;
                    }

                    return (
                      <SearchableDropdown
                        id="predefine-select"
                        inputSize={'L'}
                        onChangeSelected={(option) =>
                          onSelectPredefined(option, onChange)
                        }
                        typeDropDown="Default"
                        labelText={t('workOrder.predefined')}
                        placeholder={t('common:select')}
                        defaultValue={defaultValues?.predefine}
                        options={options}
                        minSearchLength={2}
                        isDisabled={isDisabled}
                      />
                    );
                  }}
                />

                <Controller
                  control={control}
                  name="capitol"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options =
                      defaultValues?.capitols?.map((capitol) => ({
                        label: capitol?.name || '',
                        value: capitol?.id || '',
                        data: capitol?.subCapitolsIDs || '',
                      })) || [];
                    return (
                      <DropDownInput
                        ref={capitolDropDownRef}
                        inputSize={'L'}
                        onChangeSelected={(option) =>
                          onSelectCapitol(option, onChange)
                        }
                        typeDropDown="Default"
                        labelText={t('workOrder.chapter')}
                        placeholder={t('common:labels.select')}
                        defaultValue={defaultValues?.capitol}
                        options={options}
                        isDisabled={isDisabled}
                        borderError={!capitol && !!errors.capitol}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="subCapitol"
                  render={renderSubCapitols}
                />
              </>
            )}

            {/* Multi-carretera (2026-08-18): un recorrido de VARIAS vías no
                cabe en un desplegable de una ni en un par de cajas de PK — esas
                cuatro cajas (carretera, inicio, final, y los PK del recorrido)
                enseñaban las de la PRIMERA y callaban el resto.

                Se sustituyen por una fila por carretera, que es como ya lo
                cuenta la vista previa del recorrido y se lee sin recortes.

                🔴 Solo en modo RV: en genéricas y campañas ese desplegable SÍ
                edita, y ahí no se toca nada. En RV la tarjeta no edita en línea
                —el lápiz navega al asistente y los campos van deshabilitados—
                así que aquí solo se cambia lo que se MUESTRA. */}
            <LocationGroup>
              <ConcessionWrapper>
                <Controller
                  control={control}
                  name="concession"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options =
                      defaultValues?.concessions?.reduce((acc, curr) => {
                        if (curr?.name !== 'CX') {
                          acc.push({
                            label: curr?.name || '',
                            value: curr?.id || '',
                          });
                        }

                        return acc;
                      }, [] as GroupTypes[]) || [];

                    // Si solo hay una concesión, no mostrar nada (el valor ya está establecido)
                    if (options.length === 1) {
                      return <></>;
                    }

                    // Si hay múltiples concesiones, mostrar dropdown
                    return (
                      <DropDownInput
                        inputSize={'XS'}
                        onChangeSelected={(option) =>
                          onSelectConcession(option, onChange)
                        }
                        typeDropDown="Default"
                        labelText={t('user.concession')}
                        placeholder={t('common:select')}
                        defaultValue={defaultValues?.concession}
                        options={options}
                        // RV: concesión/carretera se fijan al crear el
                        // recorrido (updateSurveillanceRoute no las acepta) —
                        // solo lectura en esta card.
                        isDisabled={
                          isDisabled || Boolean(isSurveillanceRouteOwnOrder)
                        }
                        borderError={!!errors.concession}
                      />
                    );
                  }}
                />
              </ConcessionWrapper>

              {!showRoadStretches && (
              <RoadGroup>
                <Controller
                  control={control}
                  name="road"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options =
                      roads?.map((road) => ({
                        label: road?.name || '',
                        value: road?.id || '',
                      })) || [];

                    return (
                      <DropDownInput
                        id="road-select"
                        inputSize="XS"
                        ref={roadDropDownRef}
                        onChangeSelected={(option) =>
                          onSelectRoad(option, onChange)
                        }
                        typeDropDown="Default"
                        labelText={t('workOrder.road')}
                        placeholder={t('common:select')}
                        borderError={!!errors.road}
                        defaultValue={defaultValues?.road}
                        options={options}
                        isDisabled={
                          isDisabled || Boolean(isSurveillanceRouteOwnOrder)
                        }
                      />
                    );
                  }}
                />

                <InputText
                  {...register('pkInitRoad')}
                  label={t('workOrder.start')}
                  typeInput="normal"
                  disabled
                  placeholder="000 + 000"
                  inputSize={'11'}
                  borderError={!pkInitRoad && !!errors.pkInitRoad}
                />

                <InputText
                  {...register('pkEndRoad')}
                  label={t('workOrder.end')}
                  typeInput="normal"
                  disabled
                  placeholder="000 + 000"
                  inputSize={'11'}
                  borderError={!pkEndRoad && !!errors.pkEndRoad}
                />
              </RoadGroup>
              )}
            </LocationGroup>

            {!showRoadStretches && (
              <>
            <PkGroup>
              <InputText
                {...registerPk(register, setValue, 'pkInit')}
                label={t('workOrder.pkInitial')}
                typeInput="normal"
                disabled={isDisabled}
                defaultValue={pkInit}
                inputSize={'11'}
                borderError={!pkInit && !!errors.pkInit}
              />

              <InputText
                {...registerPk(register, setValue, 'pkEnd')}
                label={t('workOrder.pkFinal')}
                typeInput="normal"
                disabled={isDisabled}
                defaultValue={pkEnd}
                inputSize={'11'}
                borderError={!pkEnd && !!errors.pkEnd}
              />
            </PkGroup>
              </>
            )}

            {/* RV (2026-07-17): clasificación fija (Operation) y tipo vial
                inexistente a nivel de recorrido (viven en cada tramo). */}
            {!isSurveillanceRouteOwnOrder && (
              <ClassificationTypeGroup>
                <Controller
                  control={control}
                  name="classification"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options =
                      classifications?.map((classification) => ({
                        label: classification?.name || '',
                        value: classification?.id || '',
                      })) || [];
                    return (
                      <DropDownInput
                        inputSize="S"
                        onChangeSelected={(option) =>
                          onChange(option?.value ?? '')
                        }
                        typeDropDown="Default"
                        labelText={t('workOrder.classification')}
                        placeholder={t('common:select')}
                        defaultValue={
                          classification || defaultValues?.classification
                        }
                        borderError={!!errors.classification}
                        options={options}
                        isDisabled={isDisabled}
                      />
                    );
                  }}
                />

                <Controller
                  name="track"
                  control={control}
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options = translateTrackOptions(
                      defaultValues?.groupTracks as CatalogEntry[] | undefined,
                      t
                    );

                    return (
                      <DropDownInput
                        labelText={t('workOrder.roadType')}
                        placeholder={'Selecciona.'}
                        inputSize={'M'}
                        options={options}
                        typeDropDown={'Default'}
                        onChangeSelected={(option) =>
                          onChange(option?.value ?? '')
                        }
                        defaultValue={defaultValues?.track}
                        borderError={!!errors.track}
                        isDisabled={isDisabled}
                      />
                    );
                  }}
                />
              </ClassificationTypeGroup>
            )}

            {!isSurveillanceRouteOwnOrder && teamField}

            {/* RV (2026-07-17): sentido/margen/carril viven en cada TRAMO
                (RouteSegment), no en el recorrido — fuera de la card. */}
            {!isSurveillanceRouteOwnOrder && (
              <LocationGroup>
                <Controller
                  control={control}
                  name="direction"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const defaultStatusValue: PartialDirection = {
                      [ActionDirection.DOWN]: 'down',
                      [ActionDirection.TOP]: 'top',
                      [ActionDirection.BOTH]: 'both',
                    };

                    const directionOptions: PartialDirection = {
                      down: ActionDirection.DOWN,
                      top: ActionDirection.TOP,
                      both: ActionDirection.BOTH,
                    };
                    return (
                      <ArrowRadioGroup
                        directions={['down', 'both', 'top']}
                        name="direction"
                        label={t('forms:workOrder.direction')}
                        defaultValue={
                          defaultStatusValue[
                            defaultValues?.direction as ActionDirection
                          ]
                        }
                        onChangeDirection={(dir) => {
                          dir
                            ? onChange(directionOptions[dir])
                            : onChange(UNSPECIFIC);
                        }}
                        borderError={!!errors.direction}
                        disabled={isDisabled}
                        directionLabels={{
                          down: roadDirections?.[0],
                          top: roadDirections?.[roadDirections?.length - 1],
                        }}
                      />
                    );
                  }}
                />

                {showMargin && (
                  <Controller
                    control={control}
                    name="margin"
                    render={({
                      field: { onChange },
                      formState: { defaultValues },
                    }) => {
                      const defaultStatusValue: PartialDirection = {
                        [Margin.BOTH]: 'both',
                        [Margin.LEFT]: 'left',
                        [Margin.RIGHT]: 'right',
                      };

                      const options: PartialDirection = {
                        both: Margin.BOTH,
                        left: Margin.LEFT,
                        right: Margin.RIGHT,
                      };

                      return (
                        <ArrowRadioGroup
                          directions={['left', 'both', 'right']}
                          name="margin"
                          label={t('forms:workOrder.margin')}
                          defaultValue={
                            defaultStatusValue[defaultValues?.margin as Margin]
                          }
                          borderError={!!errors.margin}
                          onChangeDirection={(dir) => {
                            onChange(options[dir]);
                          }}
                          disabled={isDisabled}
                        />
                      );
                    }}
                  />
                )}

                <Controller
                  name="lane"
                  control={control}
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options = translateLaneOptions(
                      defaultValues?.groupLanes as CatalogEntry[] | undefined,
                      t
                    );
                    return (
                      <DropDownInput
                        labelText={t('workOrder.lane')}
                        placeholder={'Selecciona.'}
                        inputSize={'S'}
                        options={options}
                        typeDropDown={'Default'}
                        onChangeSelected={(option) =>
                          onChange(option?.value ?? '')
                        }
                        defaultValue={defaultValues?.lane}
                        borderError={!!errors.lane}
                        isDisabled={isDisabled}
                      />
                    );
                  }}
                />
              </LocationGroup>
            )}

            {/* Las vías van LAS ÚLTIMAS y en horizontal (2026-08-18): es el
                único campo que puede ocupar más de una línea, así que en medio
                de la fila empujaba al resto y dejaba huecos al lado de los
                campos de una sola línea. */}
            {showRoadStretches && (
              <RoadStretchList>
                <RoadStretchLabel>{t('workOrder.road')}</RoadStretchLabel>
                <RoadStretchRows>
                  {routeRoadRangeRows.map((row) => (
                    <RoadStretchRow key={row.roadId}>
                      <RoadStretchName>{row.name}</RoadStretchName>
                      <RoadStretchRange>{row.range}</RoadStretchRange>
                    </RoadStretchRow>
                  ))}
                </RoadStretchRows>
              </RoadStretchList>
            )}
          </ContainerData>
          {/* D3 (2026-07-20): "Nombre del recorrido" — read-only, primero,
              en SU PROPIA fila encima de la Descripción (dentro del flex-row
              de ContainerDescription caía abajo-izquierda — cazado en la
              validación multi-resolución). Misma clave i18n que el wizard
              EditRoute/NewRoute (`UbicacioTab`). Solo en modo RV. */}
          {isSurveillanceRouteOwnOrder && (
            <RouteNameRow>
              <InputText
                label={t('pages:surveillance.name')}
                typeInput="normal"
                disabled
                value={routeName}
                inputSize={'L'}
              />
              {teamField}
            </RouteNameRow>
          )}
          <ContainerDescription>
            <InputDescription
              {...register('description')}
              label={t('workOrder.description')}
              disabled={isDisabled}
              defaultValue={description}
              borderError={!description && !!errors.description}
            />
            {/* D3 (2026-07-20): RV no usa Notas — decisión de usuario ("que
                usen descripción y ya"). Oculto en modo RV
                independientemente del valor (antes se mostraba/editaba). */}
            {!isSurveillanceRouteOwnOrder && notes && (
              <InputDescription
                {...register('notes')}
                label={t('workOrder.notes')}
                disabled={isDisabled}
                defaultValue={notes}
                borderError={!notes && !!errors.notes}
              />
            )}
            {isDisabled ? (
              <ButtonEdit
                variant={'edit'}
                onClick={
                  isSurveillanceRouteOwnOrder ? handleEditRoute : toggleEdit
                }
                type={'button'}
                disabled={
                  isSurveillanceRouteOwnOrder
                    ? !canEditRoute
                    : disabled || !canEditOrderRow(currentRole, tag)
                }
              />
            ) : (
              <ButtonSave variant={'save'} type="submit" />
            )}
          </ContainerDescription>
          {errorMessage && (
            <Typography colorText="lightCoralRed" size="XS">
              {errorMessage}
            </Typography>
          )}
        </ContainerContent>
      )}
    </ContainerOT>
  );
};
